import { useState, useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Home, MessageSquare, Target, Users, Calendar, LogOut, Menu, Zap, User as UserIcon } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetTrigger, SheetContent } from "@/components/ui/sheet";
import NotificationsMenu from "@/components/NotificationsMenu";

const NAV = [
  { to: "/sales", label: "My Dashboard", icon: Home, end: true, testid: "sales-nav-dashboard" },
  { to: "/sales/inbox", label: "My Inbox", icon: MessageSquare, testid: "sales-nav-inbox" },
  { to: "/sales/qualified", label: "My Qualified Leads", icon: Target, testid: "sales-nav-qualified" },
  { to: "/sales/leads", label: "My Leads", icon: Users, testid: "sales-nav-leads" },
  { to: "/sales/follow-ups", label: "Follow-ups", icon: Calendar, testid: "sales-nav-followups" },
];

function SidebarInner({ user, company, onLogout, onNavigate }) {
  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200">
      <div className="px-5 pt-5 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white shadow-sm">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[15px] font-bold text-slate-900 leading-tight">WaggleAI</div>
            <div className="text-[11px] text-slate-500 leading-tight">{company?.name} · Sales</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-3 space-y-0.5">
        {NAV.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testid} onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}>
            <it.icon className="w-4 h-4 shrink-0" />
            <span>{it.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-100">
        <NavLink to="/sales/profile" onClick={onNavigate} data-testid="sales-nav-profile"
          className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-slate-50 group">
          <Avatar className="w-8 h-8">
            <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs font-semibold">
              {(user?.name || "U").slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate group-hover:text-emerald-700">{user?.name}</div>
            <div className="text-[11px] text-slate-500 truncate">Sales Associate</div>
          </div>
          <Button variant="ghost" size="icon" onClick={(e)=>{e.preventDefault();e.stopPropagation();onLogout();}} data-testid="sales-logout-button" className="text-slate-500 hover:text-rose-600">
            <LogOut className="w-4 h-4" />
          </Button>
        </NavLink>
      </div>
    </div>
  );
}

export default function SalesLayout() {
  const { user, company, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [wa, setWa] = useState(null);
  useEffect(() => {
    api.get("/whatsapp/connection").then(r => setWa(r.data)).catch(()=>{});
  }, []);
  const isLive = wa?.mode === "live";
  return (
    <div className="flex h-screen bg-slate-50">
      <aside className="hidden lg:flex w-64 shrink-0">
        <SidebarInner user={user} company={company} onLogout={logout} />
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden bg-white border-b border-slate-200 px-3 h-14 flex items-center justify-between sticky top-0 z-40">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="sales-mobile-menu-button"><Menu className="w-5 h-5"/></Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SidebarInner user={user} company={company} onLogout={logout} onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white"><Zap className="w-4 h-4"/></div>
            <div className="text-sm font-bold text-slate-900">WaggleAI</div>
          </div>
          <NotificationsMenu />
        </header>
        <header className="hidden lg:flex bg-white border-b border-slate-200 h-14 items-center justify-between px-6 sticky top-0 z-30">
          <Badge variant="outline" className={isLive ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-amber-50 border-amber-300 text-amber-800"}>
            {isLive ? "LIVE MODE • Meta WhatsApp Cloud API" : "MOCK MODE • Simulated WhatsApp API"}
          </Badge>
          <NotificationsMenu />
        </header>
        <main className="flex-1 overflow-auto scrollbar-thin"><Outlet /></main>
      </div>
    </div>
  );
}
